import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Video, Sparkles, Heart, Users, ShieldCheck, ArrowRight, ClipboardCheck, Pill, Brain, HeartPulse, CalendarHeart } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AcceptedInsurance from "@/components/AcceptedInsurance";
import { Button } from "@/components/ui/button";
import { SITE, SERVICES } from "@/lib/site";

const ICONS = { ClipboardCheck, Pill, Brain, HeartPulse, Users, CalendarHeart };

const STATS = [
  { value: "10+", label: "Years of experience" },
  { value: "Ages 6+", label: "Children, teens, adults" },
  { value: "IL", label: "Statewide" },
  { value: "24h", label: "Typical reply" },
];

const PILLARS = [
  {
    icon: Heart,
    title: "Compassionate",
    body: "A judgment-free space, every visit.",
  },
  {
    icon: Users,
    title: "Family-centered",
    body: "We coordinate with your full care team.",
  },
  {
    icon: ShieldCheck,
    title: "Board-certified",
    body: "Care led by a PMHNP-BC with full practice authority.",
  },
];

export default function Home() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <Navigation />

      {/* HERO */}
      <section data-testid="hero-section" className="relative pt-28 md:pt-32 pb-10 md:pb-14">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="glow-orb top-10 -left-20 w-[420px] h-[420px] bg-primary/40" />
        <div className="glow-orb top-32 -right-32 w-[520px] h-[520px] bg-accent/25" />

        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <div
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-6 animate-fade-up"
              >
                <span className="relative flex w-2 h-2">
                  <span className="animate-ping absolute inset-0 rounded-full bg-primary/70" />
                  <span className="relative rounded-full w-2 h-2 bg-primary" />
                </span>
                Now accepting new patients · Across {SITE.state}
              </div>

              <h1
                data-testid="hero-title"
                className="font-display text-[2.6rem] sm:text-6xl lg:text-[5rem] leading-[1] tracking-tight text-balance animate-fade-up"
                style={{ animationDelay: "60ms" }}
              >
                Care that{" "}
                <em className="not-italic gradient-text font-light">listens.</em>
              </h1>

              <p
                className="mt-6 max-w-md text-lg text-muted-foreground text-pretty animate-fade-up"
                style={{ animationDelay: "120ms" }}
              >
                Compassionate psychiatric care for children, teens, and adults across {SITE.state}.
              </p>

              <div
                className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 animate-fade-up"
                style={{ animationDelay: "180ms" }}
              >
                <Button
                  asChild
                  data-testid="hero-cta-portal"
                  size="lg"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-7 h-12 shadow-[0_10px_40px_-10px_hsl(var(--primary)/0.6)]"
                >
                  <Link to="/contact">
                    New patient? Start here
                  </Link>
                </Button>
                <Button
                  asChild
                  data-testid="hero-cta-contact"
                  size="lg"
                  variant="outline"
                  className="rounded-full border-border hover:border-primary/60 hover:bg-card h-12"
                >
                  <Link to="/contact">
                    <Phone className="w-4 h-4 mr-2" /> Request appointment
                  </Link>
                </Button>
              </div>

              <div
                className="mt-10 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-5 sm:gap-x-8 sm:gap-y-4 animate-fade-up"
                style={{ animationDelay: "260ms" }}
              >
                {STATS.map((s) => (
                  <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="font-display text-2xl sm:text-3xl tracking-tight text-foreground leading-none">{s.value}</div>
                    <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1.5 sm:mt-1">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Provider visual */}
            <div className="lg:col-span-5 relative animate-fade-up max-w-md mx-auto lg:mx-0 w-full" style={{ animationDelay: "240ms" }}>
              <div className="relative rounded-[1.5rem] md:rounded-[2rem] overflow-hidden border border-border ring-glow bg-card">
                <img
                  src="/images/hero-welcome.jpg"
                  alt="Wecare Best Health — welcoming space"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />

                <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 flex items-center gap-3">
                  <div className="grid place-items-center w-11 h-11 rounded-full bg-primary text-primary-foreground">
                    <Video className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight">Care across Illinois</p>
                    <p className="text-xs text-muted-foreground">Confidential telehealth or in-office visits.</p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 hidden md:block animate-float">
                <div className="glass rounded-2xl px-4 py-3 border border-primary/30">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-primary mb-1">Provider</p>
                  <p className="font-display text-base leading-tight">{SITE.provider.name}</p>
                  <p className="text-xs text-muted-foreground">{SITE.provider.credentials}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION STRIP */}
      <section className="relative py-10 md:py-14">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6">Our mission</p>
            <p className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-balance">
              A safe space to feel
              <em className="not-italic text-primary"> heard, understood, and empowered.</em>
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-8 md:py-12">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <article
                key={p.title}
                data-testid={`pillar-${p.title.toLowerCase()}`}
                className="group relative rounded-3xl border border-border bg-card/40 p-8 overflow-hidden transition-all hover:border-primary/40 hover:bg-card"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/5 blur-2xl group-hover:bg-primary/15 transition-colors" />
                <div className="relative">
                  <span className="inline-grid place-items-center w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 text-primary mb-5">
                    <p.icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-display text-2xl tracking-tight mb-2">{p.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="relative py-10 md:py-14" id="services-preview">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">What we treat</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight text-balance max-w-xl">
                Services tailored to you.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary underline-grow"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile: horizontal scroll-snap carousel · Desktop: grid */}
          <div
            data-testid="home-services-rail"
            className="-mx-4 px-4 md:mx-0 md:px-0 flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 md:pb-0 md:overflow-visible md:grid md:grid-cols-2 lg:grid-cols-3 no-scrollbar"
          >
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Heart;
              return (
                <article
                  key={s.id}
                  data-testid={`home-service-${s.id}`}
                  className="snap-center shrink-0 w-[80%] sm:w-[60%] md:w-auto group relative flex flex-col h-auto md:h-full rounded-3xl border border-border bg-card p-6 md:p-7 hover:border-primary/40 hover:shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.25)] transition-all duration-500"
                >
                  <span
                    className="inline-grid place-items-center w-14 h-14 rounded-2xl mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3"
                    style={{
                      background: `hsl(${s.tint.bg})`,
                      color: `hsl(${s.tint.fg})`,
                    }}
                    aria-hidden="true"
                  >
                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                  </span>

                  <span className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>

                  <h3 className="font-display text-xl tracking-tight mb-2 group-hover:text-primary transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {s.description}
                  </p>

                  <div className="mt-auto pt-5 border-t border-border/70 flex items-center gap-3">
                    <Link
                      to="/services"
                      className="text-sm font-medium text-foreground/80 hover:text-primary inline-flex items-center gap-1"
                    >
                      Learn more <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <span className="text-border">·</span>
                    <Link
                      to={`/contact?service=${encodeURIComponent(s.short)}`}
                      className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline"
                    >
                      Book
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Mobile-only swipe hint */}
          <div className="md:hidden mt-3 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className="h-px w-6 bg-border" />
            Swipe to explore
            <ArrowRight className="w-3 h-3" />
          </div>
        </div>
      </section>

      {/* INSURANCE */}
      <section className="relative py-10 md:py-14">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto rounded-3xl border border-border bg-card/40 p-6 md:p-10">
            <AcceptedInsurance />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-10 md:py-14">
        <div className="container mx-auto">
          <div className="relative max-w-5xl mx-auto rounded-[2rem] overflow-hidden border border-primary/30 bg-gradient-to-br from-card via-card to-primary/10 p-6 md:p-12">
            <div className="glow-orb -top-24 -right-16 w-[420px] h-[420px] bg-primary/30" />
            <div className="glow-orb -bottom-32 -left-16 w-[420px] h-[420px] bg-accent/25" />
            <div className="relative z-10 grid md:grid-cols-12 items-center gap-8">
              <div className="md:col-span-8">
                <Sparkles className="w-7 h-7 text-primary mb-3" />
                <h2 className="font-display text-4xl md:text-5xl tracking-tight text-balance">
                  Take the first step.
                </h2>
                <p className="mt-3 text-muted-foreground max-w-md">
                  Book a confidential consultation today.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                <Button
                  asChild
                  size="lg"
                  data-testid="home-cta-schedule"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-7 h-12 w-full md:w-auto"
                >
                  <Link to="/contact">Schedule today</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  data-testid="home-cta-call"
                  className="rounded-full border-border h-12 w-full md:w-auto"
                >
                  <a href={`tel:${SITE.phoneRaw}`}>
                    <Phone className="w-4 h-4 mr-2" /> {SITE.phone}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
